"use client";

import { getTranslatedHrtName } from "@/app/directories/shop/helper";
import { formatCurrency } from "@/util/formatters";
import { PriceExplainabilityRenderer } from "@frontend/components/PriceExplainability/PriceExplainabilityRenderer";
import { useTranslations } from "@frontend/lib/next-intl-typed";
import { useBoundStore } from "@frontend/lib/stores/globalStore";
import type { CartItem } from "@frontend/lib/stores/slices/pricingMapSlice";
import { Button } from "@frontend/shared/components/ui/button";
import { cn } from "@frontend/shared/lib/utils";
import { useQueryClient } from "@tanstack/react-query";
import { useLocale } from "next-intl";
import Image from "next/image";
import { useBooking } from "../context/BookingContext";
import { getScheduleScreenTexts } from "../utils";

interface OrderSummaryProps {
  cartItems: CartItem[];
  onSkipBooking: () => void;
  orgId: number;
  primaryColor: string;
  className?: string;
}

export function OrderSummary({
  cartItems,
  onSkipBooking,
  orgId,
  className,
  primaryColor,
}: OrderSummaryProps) {
  const locale = useLocale();
  const languageCode = locale.replace("-", "_");
  const experityBooking = orgId === 24;
  const queryClient = useQueryClient();
  const t = useTranslations("Shop");
  const { selectedAppointmentTime } = useBooking();
  const isSkipSchedulingDisabled =
    queryClient.getQueryData<Record<string, boolean>>(["configFlags", orgId])
      ?.disable_skip_scheduling_on_storefront ?? false;

  const overrideAndShowContinueWithoutSchedulingButton = cartItems.some(
    (item) =>
      item.hrts[0]?.displayMetadata?.showContinueWithoutSchedulingButton ??
      false,
  );
  const showContinueWithoutSchedulingButton =
    !!overrideAndShowContinueWithoutSchedulingButton ||
    (!isSkipSchedulingDisabled && !experityBooking);
  // Per-HRT caveats shown under the skip CTA (e.g. "This provider does not
  // always schedule for this treatment."). Deliberately not localized — the
  // configured string renders as-is for every locale. See
  // getScheduleScreenTexts.
  const scheduleScreenTexts = getScheduleScreenTexts(
    cartItems.flatMap((item) => item.hrts),
  );
  // Group cart items by name and payment type
  const groupedCartItems = cartItems.reduce(
    (acc, item) => {
      const name =
        item.hrts?.length > 0
          ? item.hrts
              .map((hrt) => getTranslatedHrtName(hrt, languageCode))
              .join(", ")
          : "";

      const key = `${name}_${item.paymentType}`;
      if (!acc[key]) {
        acc[key] = { item, quantity: 1 };
      } else {
        acc[key].quantity += 1;
      }
      return acc;
    },
    {} as Record<string, { item: CartItem; quantity: number }>,
  );
  return (
    <div
      className={cn(
        "basis-1/3 lg:min-w-96 w-full flex-col gap-4 lg:flex pb-4",
        className,
      )}
    >
      <p className="font-medium text-lg mb-3.5 lg:mb-0">{t("OrderDetails")}</p>

      <div className="w-full flex flex-col gap-0 bg-white border border-[#E1E1E1] rounded-2xl mb-4">
        <div className="flex flex-col">
          <div className="flex flex-col gap-4">
            {Object.values(groupedCartItems).map(({ item, quantity }) => (
              <CartItemSummary
                key={`${item.id}_${item.paymentType}`}
                item={item}
                quantity={quantity}
              />
            ))}
          </div>
          {cartItems.length > 0 && cartItems[0].hrts.length > 0 && (
            <div className="border-b border-b-[#E1E1E1] px-6 pb-4">
              <PriceExplainabilityRenderer
                hrt={{
                  hrtId: cartItems[0].hrts[0].hrtId,
                  orgId: orgId,
                }}
              />
            </div>
          )}
        </div>

        <div className="p-6 py-4 gap-4 flex justify-center flex-col">
          <PricingSummary />
          {experityBooking && (
            <Button
              variant="default"
              onClick={onSkipBooking}
              className="w-full py-2 text-white text-sm rounded-full"
              style={{ backgroundColor: primaryColor ?? "#000000" }}
              disabled={!selectedAppointmentTime}
            >
              {t("Checkout")}
            </Button>
          )}
        </div>
      </div>

      {showContinueWithoutSchedulingButton && (
        <>
          <Button
            variant="default"
            onClick={onSkipBooking}
            className="w-full py-8 px-8 bg-black text-white text-sm rounded-full"
          >
            {t("ContinueWithoutScheduling")}
          </Button>
          {scheduleScreenTexts.map(({ text, className }) => (
            <p key={text} className={cn("text-center", className)}>
              {text}
            </p>
          ))}
        </>
      )}
    </div>
  );
}

interface CartItemSummaryProps {
  item: CartItem;
  quantity: number;
}

function CartItemSummary({ item, quantity }: CartItemSummaryProps) {
  const t = useTranslations("Shop");
  const locale = useLocale();
  const languageCode = locale.replace("-", "_");
  const isPendingPricesMode = useBoundStore.use.isPendingPricesMode();
  const translatedName = getTranslatedHrtName(item.hrts[0], languageCode);
  return (
    <div className="flex justify-between items-start p-6">
      <div className="flex h-full gap-4 w-full">
        {item.hrts[0].displayMetadata?.imageUrl ? (
          <Image
            src={item.hrts[0].displayMetadata?.imageUrl || ""}
            alt={translatedName}
            width={240}
            height={240}
            className="rounded-md object-cover w-20 h-20 flex-shrink-0"
          />
        ) : (
          <div className="w-20 h-20 bg-gray-200 rounded-md flex-shrink-0" />
        )}
        <div className="flex h-full items-center justify-between w-full">
          <p className="font-medium text-sm">
            {translatedName}
            {quantity > 1 && (
              <span className="text-gray-500 ml-1">× {quantity}</span>
            )}
          </p>

          <p className="font-base text-sm">
            {isPendingPricesMode ? (
              <span className="text-xs text-gray-600">
                <span className="text-superscriptBlue">
                  {t("PricePending")}
                </span>
              </span>
            ) : (
              formatCurrency(item.price * quantity)
            )}
          </p>
        </div>
      </div>
    </div>
  );
}

function PricingSummary() {
  const isPendingPricesMode = useBoundStore.use.isPendingPricesMode();
  const t = useTranslations("Shop");
  return (
    <div className="flex flex-row items-center">
      {isPendingPricesMode ? (
        <p className="text-xs text-gray-500">
          {t("InsuredPriceIsPendingYourAppointmentWillBeBooked")}
        </p>
      ) : (
        <p className="text-xs text-gray-500">
          {t("TheTotalPriceWillBeCalculatedAtCheckout")}
        </p>
      )}
    </div>
  );
}
